import React, { useContext, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiTrendingUp, FiZap } from "react-icons/fi";
import { SelectionContext } from "../../Context/Store";
import {
  getBillingTransactions,
  getTopupPacks,
} from "../../api/context/billing";
import { buyTopupPack } from "./checkout";
import TransactionTable from "./components/TransactionTable";
import TopupCard from "./components/TopupCard";
import TrialClaimBanner from "./components/TrialClaimBanner";

const DashboardView = ({ balance }) => {
  const { billingBalance, refreshBillingBalance } = useContext(SelectionContext);
  const activeBalance = balance || billingBalance;

  const topupRef = useRef(null);

  const scrollTo = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [processingPack, setProcessingPack] = useState(null);

  useEffect(() => {
    getTopupPacks()
      .then((packsRes) => {
        if (packsRes?.data?.success) setPacks(packsRes.data.data || []);
      })
      .finally(() => setPacksLoading(false));
  }, []);

  useEffect(() => {
    setRecentLoading(true);
    getBillingTransactions({ page: 1, pageSize: 10 })
      .then((res) => {
        if (res?.data?.success) {
          const data = res.data.data;
          const list = Array.isArray(data)
            ? data
            : data?.transactions || data?.rows || [];
          setRecent(list);
        }
      })
      .finally(() => setRecentLoading(false));
  }, [activeBalance]);

  const handleBuy = async (pack) => {
    setProcessingPack(pack.code);
    await buyTopupPack(pack.code, {
      onSuccess: () => refreshBillingBalance(),
      onDismiss: () => setProcessingPack(null),
    });
    setProcessingPack(null);
  };

  const wallet = activeBalance?.wallet;
  const totalAvailable = activeBalance?.totalAvailable ?? 0;
  const walletBalance = wallet?.balance ?? 0;
  const trial = activeBalance?.trial;
  const trialAvailable = trial?.available === true;

  // Sort by price ascending — cheapest entry pack first, premium last.
  const sortedPacks = [...packs].sort(
    (a, b) => (a.priceInPaise || 0) - (b.priceInPaise || 0)
  );

  // "Best value" is objectively the active pack with the lowest per-credit price.
  const bestValuePackCode = (() => {
    const candidates = packs.filter((p) => p.listingCount > 0);
    if (!candidates.length) return null;
    let bestCode = null;
    let bestPerCredit = Infinity;
    candidates.forEach((p) => {
      const perCredit = p.perListingPriceInPaise ?? Math.round(p.priceInPaise / p.listingCount);
      if (perCredit < bestPerCredit) {
        bestPerCredit = perCredit;
        bestCode = p.code;
      }
    });
    return bestCode;
  })();

  return (
    <div className="billing-page">
      <style>{`
        .billing-page { background: #f9fafb; min-height: calc(100vh - 70px); }
        .main-content:has(.billing-page) { background: #f9fafb; }
        .page-content:has(.billing-page) { background: #f9fafb; }

        body:has(.billing-page) .page-content,
        body:has(.billing-page) #layout-wrapper.no-topbar .page-content,
        body:has(.billing-page) #layout-wrapper .page-content {
          padding-top: 0 !important;
          padding-bottom: 24px !important;
          padding-left: 16px !important;
          padding-right: 16px !important;
        }
        body:has(.billing-page) .container-fluid,
        body:has(.billing-page) .container {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        .billing-page .page-header-sticky {
          padding: clamp(14px, 1.6vw, 24px) clamp(14px, 1.4vw, 20px) clamp(10px, 1.2vw, 18px) !important;
          margin: 0 -16px 16px !important;
          top: 0 !important;
          background: #f9fafb !important;
          border-bottom: 1px solid #eef2f7 !important;
          box-shadow: none !important;
        }
        @media (max-width: 991px) {
          .billing-page .page-header-sticky {
            padding: 16px 16px 12px !important;
          }
        }
        @media (max-width: 767.98px) {
          .billing-page .page-header-sticky {
            padding: 10px 16px 8px !important;
            margin: 0 -16px 8px !important;
            position: static !important;
          }
        }
        @media (max-width: 480px) {
          .billing-page .page-header-sticky {
            padding: 10px 16px 8px !important;
            margin: 0 -16px 8px !important;
          }
        }

        .billing-page .billing-dashboard {
          padding-left: 0 !important;
          padding-right: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 24px !important;
        }

        .billing-page .page-header-sticky h3 { font-size: 18px !important; font-weight: 700 !important; color: #111827 !important; letter-spacing: -0.01em !important; margin: 0 0 2px !important; }
        .billing-page .page-header-sticky p { font-size: 12px !important; color: #94a3b8 !important; font-weight: 500 !important; margin: 0 !important; }

        .billing-page .billing-hero-btn {
          font-size: 11px !important;
          padding: 6px 12px !important;
          border-radius: 6px !important;
        }
        .billing-page .billing-hero-number { font-size: 48px !important; }
        .billing-page .billing-hero-label { font-size: 11px !important; }
        .billing-page .billing-hero-meta { font-size: 11px !important; }

        .billing-page .billing-section-block-head h5 { font-size: 14px !important; }
        .billing-page .billing-section-block-head p { font-size: 11px !important; }

        /* Equal vertical spacing between every top-level section */
        .billing-page .billing-trial-banner,
        .billing-page .billing-hero-card,
        .billing-page .billing-section-block,
        .billing-page .billing-topup-section,
        .billing-page .billing-activity-card { margin-top: 0 !important; margin-bottom: 20px !important; }
        .billing-page .billing-dashboard > *:last-child { margin-bottom: 0 !important; }
        .billing-page .billing-topup-section .billing-section-block-head { margin-top: 0 !important; margin-bottom: 14px !important; }
        .billing-page .billing-topup-section .billing-section-block-head h5 { margin-top: 0 !important; }


        .billing-page .billing-activity-header h5 { font-size: 14px !important; }
        .billing-page .billing-activity-link { font-size: 11px !important; }

        .billing-page .billing-txn-table th { font-size: 10px !important; }
        .billing-page .billing-txn-table td { font-size: 11px !important; }
        .billing-page .billing-txn-badge { font-size: 10px !important; }

        .billing-page .billing-trial-eyebrow { font-size: 10px !important; }
        .billing-page .billing-trial-title { font-size: 15px !important; }
        .billing-page .billing-trial-sub { font-size: 11px !important; }
        .billing-page .billing-trial-btn { font-size: 12px !important; }

        @media (max-width: 767.98px) {
          .billing-page .billing-hero-card {
            padding: 18px 16px !important;
            border-radius: 14px !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
          }
          .billing-page .billing-hero-card-top { display: contents !important; }
          .billing-page .billing-hero-card-count {
            order: 2;
            text-align: center !important;
            margin-bottom: 0 !important;
          }
          .billing-page .billing-hero-card-bottom {
            order: 3;
            gap: 10px !important;
          }
          .billing-page .billing-hero-card-actions {
            order: 4;
            display: flex !important;
            width: 100% !important;
            gap: 8px !important;
            flex-wrap: nowrap !important;
            margin-top: 4px !important;
          }
          .billing-page .billing-hero-card-actions .billing-hero-btn {
            flex: 1 1 0 !important;
            padding: 8px 10px !important;
            font-size: 11px !important;
            text-align: center;
            border-radius: 8px !important;
          }
          .billing-page .billing-hero-number { font-size: 38px !important; }
          .billing-page .billing-hero-label { font-size: 12px !important; opacity: 0.9; }
          .billing-page .billing-hero-meta {
            flex-direction: column !important;
            gap: 6px !important;
            font-size: 11px !important;
          }
          .billing-page .billing-trial-banner {
            padding: 14px 16px !important;
            border-radius: 12px !important;
          }
          .billing-page .billing-trial-title { font-size: 14px !important; }

          .billing-page .billing-section-block,
          .billing-page .billing-activity-card {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            border-radius: 0 !important;
          }
          .billing-page .billing-topup-section {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
            width: 100vw !important;
            max-width: 100vw !important;
            margin-left: calc(50% - 50vw) !important;
            margin-right: calc(50% - 50vw) !important;
          }
          .billing-page .billing-topup-section .billing-section-block-head {
            padding: 0 16px !important;
            margin-bottom: 18px !important;
          }
          .billing-page .billing-topup-section .billing-topup-grid {
            padding: 20px 16px 12px !important;
            scroll-padding-left: 16px !important;
            scroll-padding-right: 16px !important;
            /* custom.scss caps the mobile grid at 360px wide + auto-centers it,
               which stacked an extra margin on top of our padding. Nuke that. */
            max-width: none !important;
            margin: 0 !important;
          }
          .billing-page .billing-topup-section .billing-topup-grid > * {
            scroll-snap-align: start !important;
          }
          .billing-page .billing-topup-grid > * {
            flex: 0 0 78% !important;
            max-width: 78% !important;
          }
          .billing-page .billing-section-block-head,
          .billing-page .billing-activity-header {
            padding: 0 !important;
            margin-bottom: 12px !important;
          }

          .billing-page .billing-topup-grid {
            display: flex !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            gap: 12px !important;
            padding: 16px 12px 12px 0 !important;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .billing-page .billing-topup-grid::-webkit-scrollbar { display: none; }
          .billing-page .billing-topup-grid > * {
            flex: 0 0 55% !important;
            max-width: 55% !important;
            scroll-snap-align: start;
          }

          .billing-page .billing-topup-card { padding: 12px !important; }
          .billing-page .billing-txn-table th,
          .billing-page .billing-txn-table td { padding: 8px 10px !important; }
        }
      `}</style>

      <div className="page-header-sticky">
        <h3 className="fw-bold">Billing & AI photoshoots</h3>
        <p>Manage your top-up credits.</p>
      </div>

      <div className="billing-dashboard">
        {trialAvailable && <TrialClaimBanner trial={trial} />}

      <div className="billing-hero-card">
        <div className="billing-hero-card-top">
          <div className="billing-hero-card-actions">
            <button
              type="button"
              className="billing-hero-btn"
              onClick={() => scrollTo(topupRef)}
            >
              Buy credits
            </button>
          </div>
        </div>

        <div className="billing-hero-card-count">
          <div className="billing-hero-number">{totalAvailable}</div>
          <div className="billing-hero-label">credits available</div>
        </div>

        <div className="billing-hero-card-bottom">
          <div className="billing-hero-meta billing-hero-meta-empty">
            <span>
              <FiTrendingUp />
              {walletBalance > 0
                ? `${walletBalance} top-up credit${walletBalance === 1 ? "" : "s"} in your wallet`
                : "Buy top-up credits anytime to generate AI listings."}
            </span>
          </div>
        </div>
      </div>

      <div className="billing-section-block billing-topup-section" ref={topupRef}>
        <div className="billing-section-block-head">
          <h5>
            <FiZap /> Buy top-up credits
          </h5>
          <p>Top-up credits never expire.</p>
        </div>
        {packsLoading && !packs.length ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div className="billing-topup-grid">
            {sortedPacks.map((pack) => (
              <TopupCard
                key={pack.code}
                pack={{ ...pack, isBestValue: pack.code === bestValuePackCode }}
                isProcessing={processingPack === pack.code}
                onBuy={handleBuy}
              />
            ))}
          </div>
        )}
      </div>

      <div className="billing-activity-card">
        <div className="billing-activity-header">
          <h5>Recent activity</h5>
          <Link to="/billing/history" className="billing-activity-link">
            View all →
          </Link>
        </div>
        <TransactionTable rows={recent} loading={recentLoading} />
      </div>
      </div>
    </div>
  );
};

export default DashboardView;
